/**
 * QBR schemas (portfolio-group-qbr-customizations Phase 4).
 *
 * Backend routes: `backend/src/handlers/qbr.ts` (GET/PUT /api/qbr/strategy,
 * GET /api/qbr/roadmap, GET/PUT /api/qbr/last-quarter, POST /api/qbr/generate).
 *
 * Tolerant boundary (`z.looseObject`) per the `InitiativeSchema` convention —
 * unknown fields pass through rather than failing validation, since the
 * backend response shapes may grow additive fields ahead of a frontend
 * schema update.
 */
import { z } from 'zod';

/** ProvenanceValue<T> — every leaf value the backend tags 'real' | 'derived' | 'tbd'. */
const ProvenanceValueSchema = z.looseObject({
  value: z.union([z.string(), z.number()]).nullable(),
  provenance: z.enum(['real', 'derived', 'tbd']),
});

/**
 * GET /api/qbr/strategy — merges the OpCo Strategy record with
 * `moatSectionEnabled` + `qbrConfigured` from the group's QBR config. This is
 * the SINGLE source of truth the Company Strategy card AND the Generate
 * button both read (see handlers/qbr.ts doc comment) — never split across
 * two endpoints.
 */
export const OpcoStrategyResponseSchema = z.looseObject({
  moatSummary: z.string().optional().default(''),
  highestConvictionMove: z.string().optional().default(''),
  forwardAllocation: z
    .looseObject({
      next6mo: z.number().optional(),
      next12mo: z.number().optional(),
      next18mo: z.number().optional(),
    })
    .optional(),
  moatSectionEnabled: z.boolean(),
  qbrConfigured: z.boolean(),
});

export type OpcoStrategyResponse = z.infer<typeof OpcoStrategyResponseSchema>;

/** GET /api/qbr/roadmap — category x horizon grid (see backend/src/qbr/roadmap.ts). */
const RoadmapGridEntrySchema = z.looseObject({
  id: z.string(),
  unscheduled: z.boolean(),
});

const RoadmapGridHorizonsSchema = z.looseObject({
  next6mo: z.array(RoadmapGridEntrySchema),
  next12mo: z.array(RoadmapGridEntrySchema),
  next18mo: z.array(RoadmapGridEntrySchema),
  beyond18mo: z.array(RoadmapGridEntrySchema),
  unscheduled: z.array(RoadmapGridEntrySchema),
});

export const RoadmapViewResponseSchema = z.looseObject({
  roadmapGrid: z.looseObject({
    stabilize_scale: RoadmapGridHorizonsSchema,
    grow_expand: RoadmapGridHorizonsSchema,
    transformational_bet: RoadmapGridHorizonsSchema,
    unmapped: RoadmapGridHorizonsSchema,
  }),
});

export type RoadmapViewResponse = z.infer<typeof RoadmapViewResponseSchema>;

/**
 * GET /api/qbr/last-quarter — assembled, provenance-tagged draft rows.
 *
 * `stageFrom`/`stageTo`/`stageChangedAt` (progression) and `schedule`/
 * `liveDate` are derived read-outs added by qbr-improvements, which retired the
 * CEO-entered `status` field. They are `.optional()` so the frontend keeps
 * rendering against an older deployed backend that doesn't send them yet.
 */
const LastQuarterEntrySchema = z.looseObject({
  initiativeId: z.string(),
  newStage: ProvenanceValueSchema,
  newHealth: ProvenanceValueSchema,
  timestamp: ProvenanceValueSchema,
  ceoNotes: ProvenanceValueSchema,
  commitment: ProvenanceValueSchema,
  stageFrom: ProvenanceValueSchema.optional(),
  stageTo: ProvenanceValueSchema.optional(),
  stageChangedAt: ProvenanceValueSchema.optional(),
  schedule: ProvenanceValueSchema.optional(),
  liveDate: ProvenanceValueSchema.optional(),
});

export const LastQuarterResponseSchema = z.looseObject({
  lastQuarter: z.array(LastQuarterEntrySchema),
});

export type LastQuarterResponse = z.infer<typeof LastQuarterResponseSchema>;
export type LastQuarterEntry = z.infer<typeof LastQuarterEntrySchema>;

/** POST /api/qbr/generate — `{ downloadUrl: string }` (a presigned S3 GET URL). */
export const GenerateQbrResponseSchema = z.looseObject({
  downloadUrl: z.string(),
});

export type GenerateQbrResponse = z.infer<typeof GenerateQbrResponseSchema>;
